// The one status vocabulary of the workspace. Backend states (documents,
// jobs, readiness, requirements) are mapped here and nowhere else, so every
// page says "Needs review" for the same thing and draws it the same way.
// Every status has a glyph and a label: colour is never the only signal.
import type { DocumentStatus, ReadinessStatus, Requirement } from "./api/types";
import { w } from "./i18n";

export type UserStatus = "not_started" | "processing" | "ready" | "partial" | "missing" | "generated"
  | "needs_review" | "approved" | "failed";

export const STATUS_GLYPH: Record<UserStatus, string> = {
  not_started: "○", processing: "◐", ready: "✓", partial: "◑", missing: "!", generated: "●",
  needs_review: "!", approved: "✓", failed: "✕",
};

// Tones map to CSS tokens (--ws-tone-*): neutral, info, positive, caution, negative.
export const STATUS_TONE: Record<UserStatus, "neutral" | "info" | "positive" | "caution" | "negative"> = {
  not_started: "neutral", processing: "info", ready: "positive", partial: "caution", missing: "caution",
  generated: "info", needs_review: "caution", approved: "positive", failed: "negative",
};

export function documentStatus(status: DocumentStatus): UserStatus {
  return ({ queued: "processing", processing: "processing", ready: "ready", needs_review: "needs_review",
    failed: "failed" } as const)[status] ?? "not_started";
}

export function readinessStatus(status: ReadinessStatus): UserStatus {
  return ({ READY: "ready", PARTIALLY_READY: "partial", NOT_READY: "missing" } as const)[status];
}

export function requirementStatus(state: Requirement["state"]): UserStatus {
  return ({ satisfied: "ready", pending: "needs_review", missing: "missing" } as const)[state];
}

export function isActive(status: DocumentStatus): boolean {
  return status === "queued" || status === "processing";
}

/** Section-level wording: "Required inputs missing", not a bare "Missing". */
export function readinessLabel(status: ReadinessStatus): string {
  return w(`readiness.${status}`);
}
